import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { BrandingRequest, IBranding } from "../library/models/tenantBranding";
import { BaseService } from "./baseService";

/**
 * The clinic's brand colours and logo.
 *
 * Neither read nor write names a clinic: the API takes it from the session, so this returns
 * the caller's own branding and writes to nobody else's. Reading needs any signed-in member;
 * writing is the clinic admin's alone, and the API refuses either way rather than trusting
 * the button to have been hidden.
 *
 * The two images are the exception to that shape. Their uploads are multipart and their
 * downloads are bytes, so neither is a JSON call — but both live here anyway, because a
 * second service class for "the same clinic's branding, in a different content type" would
 * be filing by encoding rather than by subject.
 *
 * The one setting that is *not* here is the pair of watermark numbers: they are ordinary
 * JSON and ride `saveBrandingMutation` with the colours.
 */
export class TenantBrandingService extends BaseService {
    /**
     * Keyed by the clinic even though the request does not name one.
     *
     * The route takes the tenant from the session, so the key is the only thing standing
     * between two sessions in one page load: without it both read the same cache entry, and
     * somebody signing out of one clinic and into another is served -- and caches to
     * localStorage under the new clinic's id -- the colours of the old one. Hand-written
     * rather than built with createGetQuery, whose key is the URL and its query parameters,
     * because the tenant is neither.
     */
    getBranding(tenantId: number | null): UseQueryOptions<IBranding, Error> {
        return {
            queryKey: ['/tenant-branding', tenantId],
            queryFn: (): Promise<IBranding> => this.get<IBranding>('/tenant-branding'),
        };
    }

    /**
     * Sends both colours. An empty string resets that colour to the platform's, which is
     * why this is a PUT of the pair rather than a patch of one -- "no colour chosen" has to
     * be expressible, and omitting a field cannot say it.
     *
     * Hand-written rather than built with createMutation, which only speaks POST/PATCH/DELETE
     * shapes. Resolves with the stored result so the caller renders what was saved rather
     * than trusting its own draft.
     */
    saveBrandingMutation(): UseMutationOptions<IBranding, Error, BrandingRequest> {
        return {
            mutationFn: (payload: BrandingRequest): Promise<IBranding> =>
                this.put<IBranding>('/tenant-branding', payload),
        };
    }

    /**
     * Fetches the stored logo back as a file, so the crop dialog can reopen it.
     *
     * Deliberately not a React Query option object like everything else here: this is a
     * one-shot fetch fired by a button, not state a page renders, and caching it would mean
     * an admin re-editing straight after a save gets the previous image back.
     *
     * The URL carries the ETag as `?v=` for the same reason the `<img>` does -- the path is
     * stable across uploads and the response is cached for five minutes, so editing without
     * it would hand back the logo before last.
     *
     * The clinic is named in the path because this route is the public one; it is the only
     * way to read the bytes, and the session's own id is what gets passed.
     */
    async fetchLogoFile(tenantId: number, etag: string | null): Promise<File> {
        const query: string = etag ? `?v=${etag}` : '';

        const blob: Blob = await this.get<Blob>(`/tenant-branding/${tenantId}/logo${query}`, {
            responseType: 'blob',
        });

        return new File([blob], 'logo.png', { type: blob.type || 'image/png' });
    }

    /**
     * Uploads a logo.
     *
     * `Content-Type: undefined` is not a mistake. This axios instance defaults every request
     * to `application/json`, and a multipart body needs a boundary parameter that only the
     * browser can generate -- clearing the header is what lets it do so. Setting
     * `multipart/form-data` by hand instead produces a boundary-less header and a body the
     * server cannot parse.
     */
    uploadLogoMutation(): UseMutationOptions<void, Error, File> {
        return {
            mutationFn: (file: File): Promise<void> => {
                const body = new FormData();
                body.append('file', file);

                return this.put<void>('/tenant-branding/logo', body, {
                    headers: { 'Content-Type': undefined },
                });
            },
        };
    }

    /** Removes the logo and falls back to the platform mark. Colours are untouched. */
    removeLogoMutation(): UseMutationOptions<void, Error, void> {
        return {
            mutationFn: (): Promise<void> => this.delete<void>('/tenant-branding/logo'),
        };
    }

    /**
     * Fetches the stored favicon as a file, so the crop dialog can reopen on it.
     *
     * The favicon's three routes are the logo's above rather than the watermark's below, and
     * the difference is which one the browser fetches for itself: a `<link rel="icon">` sends
     * no token, exactly as an `<img src>` does not, so the read is public and names the clinic
     * in the path. The ETag rides as `?v=` for the same reason it does there.
     */
    async fetchFaviconFile(tenantId: number, etag: string | null): Promise<File> {
        const query: string = etag ? `?v=${etag}` : '';

        const blob: Blob = await this.get<Blob>(`/tenant-branding/${tenantId}/favicon${query}`, {
            responseType: 'blob',
        });

        return new File([blob], 'favicon.png', { type: blob.type || 'image/png' });
    }

    /** Uploads a favicon. `Content-Type: undefined` for the reason given on the logo above. */
    uploadFaviconMutation(): UseMutationOptions<void, Error, File> {
        return {
            mutationFn: (file: File): Promise<void> => {
                const body = new FormData();
                body.append('file', file);

                return this.put<void>('/tenant-branding/favicon', body, {
                    headers: { 'Content-Type': undefined },
                });
            },
        };
    }

    /** Removes the favicon. The tab falls back to the clinic's logo; nothing else changes. */
    removeFaviconMutation(): UseMutationOptions<void, Error, void> {
        return {
            mutationFn: (): Promise<void> => this.delete<void>('/tenant-branding/favicon'),
        };
    }

    /**
     * Fetches the mark printed behind the clinic's documents, as a file.
     *
     * The watermark's three routes mirror the logo's above with one deliberate difference:
     * **this one is not public and names no clinic.** The logo's has to be both — a browser
     * rendering an `<img src>` sends no token, and the same URL is the masthead of every
     * email the clinic sends. Nothing outside the app ever fetches a watermark, so it takes
     * the clinic from the session like the rest of the branding.
     *
     * Which is why the branding card cannot point an `<img>` at it and has to hold an object
     * URL instead — the same thing `MedicalRecordsTimeline` does with a PDF, and for the same
     * reason. The ETag rides as `?v=` so a re-crop is not hidden behind the five-minute
     * `max-age`.
     */
    async fetchWatermarkFile(etag: string | null): Promise<File> {
        const query: string = etag ? `?v=${etag}` : '';

        const blob: Blob = await this.get<Blob>(`/tenant-branding/watermark${query}`, {
            responseType: 'blob',
        });

        return new File([blob], 'watermark.png', { type: blob.type || 'image/png' });
    }

    /** `Content-Type: undefined` for the reason `uploadLogoMutation` gives. */
    uploadWatermarkMutation(): UseMutationOptions<void, Error, File> {
        return {
            mutationFn: (file: File): Promise<void> => {
                const body = new FormData();
                body.append('file', file);

                return this.put<void>('/tenant-branding/watermark', body, {
                    headers: { 'Content-Type': undefined },
                });
            },
        };
    }

    /**
     * Removes the watermark. The clinic's logo is printed instead, at the same opacity and
     * scale — those are settings the clinic chose and are not thrown away with the picture.
     */
    removeWatermarkMutation(): UseMutationOptions<void, Error, void> {
        return {
            mutationFn: (): Promise<void> => this.delete<void>('/tenant-branding/watermark'),
        };
    }
}
